import React, { ReactNode, useMemo } from "react";
import {
  mdiChevronDown,
  mdiChevronRight,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiMinusBoxOutline,
} from "@mdi/js";
import { cn, incPanelColorLevel, panelBackgroundStyles } from "../../../styles";
import { Icon } from "../../../atoms/Icon";
import { useDataTableContext } from "../context";
import { GroupNode, getTotalRows } from "../hooks/useGrouping";

export interface GroupProps<TRow> {
  node: GroupNode<TRow>;
  depth?: number;
  children?: ReactNode;
  className?: string;
}

export function Group<TRow>({
  node,
  depth = 0,
  children,
  className,
}: GroupProps<TRow>) {
  const { table, color, dense, expansion, selection } = useDataTableContext<TRow>();

  const isExpanded = expansion?.isExpanded(node.key) ?? true;
  const groupColumn = table.columns.find((col) => col.group);
  const visibleColumns = table.columns.filter((col) => !col.hidden);
  const colSpan = visibleColumns.length + (selection ? 1 : 0);
  const groupColor = incPanelColorLevel(color, 1);

  // Get all rows in this group (including nested groups)
  const allRows = useMemo(() => getTotalRows(node), [node]);

  // Calculate selection state for this group
  const selectionState = useMemo(() => {
    if (!selection || allRows.length === 0) return "none";
    const selectedCount = allRows.filter((row) => selection.isSelected(row)).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === allRows.length) return "all";
    return "some";
  }, [selection, allRows]);

  const handleToggle = () => {
    expansion?.toggle(node.key);
  };

  const handleToggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selection) return;

    if (selectionState === "all") {
      // Deselect all rows in this group
      allRows.forEach((row) => {
        if (selection.isSelected(row)) {
          selection.toggle(row);
        }
      });
    } else {
      // Select all rows in this group
      allRows.forEach((row) => {
        if (!selection.isSelected(row)) {
          selection.toggle(row);
        }
      });
    }
  };

  const renderLabel = (): ReactNode => {
    if (groupColumn?.renderGroup) {
      return groupColumn.renderGroup(node);
    }
    return node.label ?? node.labelKey ?? node.key;
  };

  const getCheckboxIcon = () => {
    switch (selectionState) {
      case "all":
        return mdiCheckboxMarked;
      case "some":
        return mdiMinusBoxOutline;
      default:
        return mdiCheckboxBlankOutline;
    }
  };

  return (
    <>
      <tr
        className={cn(
          "border-b border-back-200 dark:border-back-700 cursor-pointer",
          panelBackgroundStyles[groupColor],
          className
        )}
        onClick={handleToggle}
      >
        {selection && (
          <td className="p-0">
            <button
              onClick={handleToggleSelection}
              className={cn(
                "flex items-center justify-center w-full cursor-pointer",
                dense ? "px-2 py-1.5" : "px-4 py-2",
                selectionState === "all"
                  ? "text-primary-600"
                  : selectionState === "some"
                  ? "text-primary-400"
                  : "text-front-300 hover:text-front-400"
              )}
            >
              <Icon path={getCheckboxIcon()} size={18} />
            </button>
          </td>
        )}
        <td
          colSpan={visibleColumns.length}
          className={cn(dense ? "px-2 py-1.5" : "px-4 py-2")}
        >
          <div
            className="flex items-center gap-2 font-medium"
            style={{ paddingLeft: depth * 24 }}
          >
            <Icon
              path={isExpanded ? mdiChevronDown : mdiChevronRight}
              size={18}
              className="text-front-500"
            />
            {renderLabel()}
            <span className="text-front-400 text-sm">
              ({allRows.length})
            </span>
          </div>
        </td>
      </tr>
      {isExpanded && children}
    </>
  );
}
